import { useMutation } from "@apollo/client";
import {
    ACA_ADMIN_SETTINGS_SNAPSHOT,
    ACA_ADMIN_CREATE_PROVIDER,
    ACA_ADMIN_SET_PROVIDER_STATUS,
    ACA_ADMIN_ADD_PROVIDER_STATE,
    ACA_ADMIN_REMOVE_PROVIDER_STATE,
    ACA_ADMIN_ADD_FAILED_REASON,
    ACA_ADMIN_SET_FAILED_REASON_STATUS,
    ACA_ADMIN_ADD_COMPLIANCE_REASON,
    ACA_ADMIN_SET_COMPLIANCE_REASON_STATUS,
    ACA_ADMIN_CREATE_TIER2_AGENT,
    ACA_ADMIN_ADD_TIER2_LICENSE,
    ACA_ADMIN_SET_TIER2_AGENT_ACTIVE,
    ACA_ADMIN_SET_TIER2_AGENT_NPN_NUMBER,
    ACA_ADMIN_UPDATE_TIER2_LICENSE,
} from "../../schemas/aca";
import React, { useEffect, useMemo, useState } from "react";
import { IonAccordionGroup, IonContent, IonLoading, IonPage, useIonAlert } from "@ionic/react";
import { AcaHeader } from "./AcaHeader";
import "./AcaSettings.css";
import type { ToggleCustomEvent } from "@ionic/core/components";
import type {
    AgentLicenseUiModel,
    Code,
    EditableAgentLicenseDraft,
    NewAgentLicenseForm,
    Provider,
    Reason,
    Snapshot,
    Tier2AgentUiModel,
    Tier2UserOption,
} from "./AcaSettingsTypes";
import AcaSettingsComplianceSection from "./components/AcaSettingsComplianceSection";
import AcaSettingsFailedReasonSection from "./components/AcaSettingsFailedReasonSection";
import AcaSettingsProvidersSection from "./components/AcaSettingsProvidersSection";
import AcaSettingsProviderStatesSection from "./components/AcaSettingsProviderStatesSection";
import AcaSettingsTier2AgentsSection from "./components/AcaSettingsTier2AgentsSection";
import AcaSettingsCreateAlertFlow from "./components/AcaSettingsCreateAlertFlow";
import ModalManageProviderMappings from "./modals/ModalManageProviderMappings";
import ModalAddTier2Agent from "./modals/ModalAddTier2Agent";
import ModalManageTier2Licenses from "./modals/ModalManageTier2Licenses";

const validateGlobalNpn = (value: string): string => {
    const raw = (value || "").trim();

    if (!raw) {
        return "NPN is required.";
    }
    if (!/^[0-9]+$/.test(raw)) {
        return "NPN must contain digits only.";
    }
    if (raw.length > 10) {
        return "NPN must be 10 digits or less.";
    }
    if (raw.length > 1 && raw.startsWith("0")) {
        return "NPN cannot have leading zeros.";
    }
    return "";
};

const normalizeDupKey = (value: any): string => {
    return (value ?? "").toString().trim().toLowerCase();
};

const AcaSettings: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [presentAlert] = useIonAlert();

    const [snapshot, setSnapshot] = useState<Snapshot | undefined>(undefined);

    // inputs
    const [newProviderName, setNewProviderName] = useState("");
    const [newReasonDesc, setNewReasonDesc] = useState("");
    const [newComplianceDesc, setNewComplianceDesc] = useState("");

    // sequential alert flows
    const [provPromptOpen, setProvPromptOpen] = useState(false);
    const [provConfirmOpen, setProvConfirmOpen] = useState(false);
    const [provConfirmQueued, setProvConfirmQueued] = useState(false);
    const [pendingProviderName, setPendingProviderName] = useState<string>("");

    const [reasonPromptOpen, setReasonPromptOpen] = useState(false);
    const [reasonConfirmOpen, setReasonConfirmOpen] = useState(false);
    const [reasonConfirmQueued, setReasonConfirmQueued] = useState(false);
    const [pendingReasonDesc, setPendingReasonDesc] = useState<string>("");

    const [compPromptOpen, setCompPromptOpen] = useState(false);
    const [compConfirmOpen, setCompConfirmOpen] = useState(false);
    const [compConfirmQueued, setCompConfirmQueued] = useState(false);
    const [pendingComplianceDesc, setPendingComplianceDesc] = useState<string>("");

    // Provider → State modal state
    const [mappingSelectOpen, setMappingSelectOpen] = useState(false);
    const [pendingMappingState, setPendingMappingState] = useState<string | null>(null);

    //tier 2 agent states
    const [tier2Agents, setTier2Agents] = useState<Tier2AgentUiModel[]>([]);

    // Add Tier 2 Agent modal state
    const [tier2AddOpen, setTier2AddOpen] = useState<boolean>(false);
    const [tier2NewUserId, setTier2NewUserId] = useState<number | null>(null);
    const [tier2NewNpn, setTier2NewNpn] = useState<string>("");
    const [tier2UserOptions, setTier2UserOptions] = useState<Tier2UserOption[]>([]);

    //licenses being added on the Add-Agent modal
    const [newAgentLicenses, setNewAgentLicenses] = useState<NewAgentLicenseForm[]>([]);

    // Global NPN inline edit (Manage Licenses modal)
    const [isEditingAgentNpn, setIsEditingAgentNpn] = useState<boolean>(false);
    const [agentNpnDraft, setAgentNpnDraft] = useState<string>("");
    const [agentNpnDraftError, setAgentNpnDraftError] = useState<string>("");

    // Existing license inline edit (Manage Licenses modal)
    const [editingLicenseId, setEditingLicenseId] = useState<number | null>(null);
    const [editingLicenseDraft, setEditingLicenseDraft] = useState<EditableAgentLicenseDraft | null>(null);
    const [editingLicenseDraftError, setEditingLicenseDraftError] = useState<string>("");

    const [licenseModalOpen, setLicenseModalOpen] = useState(false);
    const [selectedAgentForLicenses, setSelectedAgentForLicenses] = useState<Tier2AgentUiModel | null>(null);

    const [newLicenseState, setNewLicenseState] = useState<string>("");
    const [newLicenseNumber, setNewLicenseNumber] = useState<string>("");
    const [newLicenseExpiry, setNewLicenseExpiry] = useState<string>("");

    //Global NPN display error messages
    const tier2NewNpnError = useMemo(() => {
        return validateGlobalNpn(tier2NewNpn);
    }, [tier2NewNpn]);

    // mutations
    const [loadSnapshot] = useMutation(ACA_ADMIN_SETTINGS_SNAPSHOT);
    const [createProvider] = useMutation(ACA_ADMIN_CREATE_PROVIDER);
    const [setProviderStatus] = useMutation(ACA_ADMIN_SET_PROVIDER_STATUS);
    const [addProviderState] = useMutation(ACA_ADMIN_ADD_PROVIDER_STATE);
    const [removeProviderState] = useMutation(ACA_ADMIN_REMOVE_PROVIDER_STATE);
    const [addFailedReason] = useMutation(ACA_ADMIN_ADD_FAILED_REASON);
    const [setFailedReasonStatus] = useMutation(ACA_ADMIN_SET_FAILED_REASON_STATUS);
    const [addComplianceReason] = useMutation(ACA_ADMIN_ADD_COMPLIANCE_REASON);
    const [setComplianceReasonStatus] = useMutation(ACA_ADMIN_SET_COMPLIANCE_REASON_STATUS);
    const [createTier2Agent] = useMutation(ACA_ADMIN_CREATE_TIER2_AGENT);
    const [addTier2License] = useMutation(ACA_ADMIN_ADD_TIER2_LICENSE);
    const [setTier2AgentActive] = useMutation(ACA_ADMIN_SET_TIER2_AGENT_ACTIVE);
    const [setTier2AgentNpnNumber] = useMutation(ACA_ADMIN_SET_TIER2_AGENT_NPN_NUMBER);
    const [updateTier2License] = useMutation(ACA_ADMIN_UPDATE_TIER2_LICENSE);

    const refresh = async () => {
        setIsLoading(true);
        try {
            const { data } = await loadSnapshot();
            const res = data?.acaAdminSettingsSnapshot;
            if (!res?.success) {
                presentAlert(res?.error || "Failed to load settings");
            } else {
                setSnapshot({
                    providers: res.providers,
                    mappings: res.mappings,
                    codes: res.codes,
                    complianceReasons: res.complianceReasons,
                    tier2Agents: res.tier2Agents,
                });
                if (Array.isArray(res.tier2Agents)) {
                    setTier2Agents(res.tier2Agents);
                }

                if (Array.isArray(res.tier2UserOptions)) {
                    setTier2UserOptions(res.tier2UserOptions);
                }
            }
        } catch (e: any) {
            console.error(e);
            presentAlert(e.message || "Failed to load settings");
        } finally {
            setIsLoading(false);
        }
    };

    // refresh without isLoading for provider to state snapshot
    const softRefresh = async () => {
        const { data } = await loadSnapshot();
        const res = data?.acaAdminSettingsSnapshot;
        if (res?.success) {
            setSnapshot({
                providers: res.providers,
                mappings: res.mappings,
                codes: res.codes,
                complianceReasons: res.complianceReasons,
                tier2Agents: res.tier2Agents,
            });
            setTier2Agents(res.tier2Agents || []);
            setTier2UserOptions(res.tier2UserOptions || []);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const providers = snapshot?.providers || [];
    const mappings = snapshot?.mappings || [];
    const codes = snapshot?.codes || [];
    const complianceReasons = snapshot?.complianceReasons || [];

    // helpers
    const getProviderName = (id: number) => providers.find((p) => p.id === id)?.name || `Provider ${id}`;

    const getProviderStates = (providerId: number): string[] => {
        return Array.from(
            new Set(
                mappings
                    .filter((mapping) => mapping.providerId === providerId)
                    .map((mapping) => mapping.state)
            )
        );
    };

    // compute summary of licensed states by agents
    const computeStateSummary = (licenses?: AgentLicenseUiModel[]): string => {
        if (!licenses || licenses.length === 0) {
            return "";
        }
        const activeStates = Array.from(
            new Set(
                licenses
                    .filter((l) => l.active)
                    .map((l) => l.stateCode)
            )
        );
        return activeStates.join(", ");
    };


    // Reset add-agent form (opening/closing modal)
    const resetNewAgentForm = () => {
        setTier2NewUserId(null);
        setTier2NewNpn("");
        setNewAgentLicenses([
            {
                id: 1,
                stateCode: "",
                licenseNumber: "",
                expirationDate: "",
                active: true,
            },
        ]);
    };

    const handleCloseAddTier2AgentModal = () => {
        setTier2AddOpen(false);
        resetNewAgentForm();
    };

    const handleOpenAddTier2AgentModal = () => {
        resetNewAgentForm();
        setTier2AddOpen(true);
    };

    const addNewAgentLicenseRow = () => {
        setNewAgentLicenses((prev) => {
            const nextId = prev.length ? Math.max(...prev.map((l) => l.id)) + 1 : 1;
            return [
                ...prev,
                {
                    id: nextId,
                    stateCode: "",
                    licenseNumber: "",
                    expirationDate: "",
                    active: true,
                },
            ];
        });
    };

    const updateNewAgentLicenseRow = (
        rowId: number,
        patch: Partial<Omit<NewAgentLicenseForm, "id">>
    ) => {
        setNewAgentLicenses((prev) =>
            prev.map((row) => (row.id === rowId ? { ...row, ...patch } : row))
        );
    };

    const removeNewAgentLicenseRow = (rowId: number) => {
        setNewAgentLicenses((prev) =>
            prev.length <= 1 ? prev : prev.filter((row) => row.id !== rowId)
        );
    };

    // Toggle Tier 2 agent status active/inactive (Not tied with new AMS User management, will be tied in with ACA lead disposition controls)
    const onToggleTier2AgentActive = async (
        agent: Tier2AgentUiModel,
        nextActive: boolean
    ) => {
        setIsLoading(true);
        try {
            const { data } = await setTier2AgentActive({
                variables: { agentId: agent.id, active: nextActive },
            });

            const res = data?.acaAdminSetTier2AgentActive;
            if (!res?.success) {
                presentAlert(res?.error || "Failed to update agent status");
                return;
            }

            setTier2Agents((prev) =>
                prev.map((a) =>
                    a.id === agent.id ? { ...a, agentActive: nextActive } : a
                )
            );

            setSelectedAgentForLicenses((prev) => {
                if (!prev) {
                    return prev;
                }
                if (prev.id !== agent.id) {
                    return prev;
                }
                return { ...prev, agentActive: nextActive };
            });
        } catch (e: any) {
            console.error(e);
            presentAlert(e.message || "Failed to update agent status");
        } finally {
            setIsLoading(false);
        }
    };

    const onToggleProvider = async (p: Provider, ev?: ToggleCustomEvent) => {
        if (ev) {
            ev.target.checked = (p.status === "ACTIVE");
        }
        const next = p.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

        if (next === "INACTIVE") {
            const states = getProviderStates(p.id);
            const stateList = states.length ? states.join(", ") : "None";
            presentAlert({
                header: "Deactivate provider?",
                message:
                    `Set “${p.name}” to INACTIVE?\n\n` +
                    `This will remove ${states.length} state association(s): ${stateList}.`,
                buttons: [
                    { text: "Cancel", role: "cancel" },
                    {
                        text: "Deactivate",
                        role: "destructive",
                        handler: async () => {
                            setIsLoading(true);
                            try {
                                // remove all mappings for this provider
                                if (states.length) {
                                    setSnapshot((prev) =>
                                        prev ? { ...prev, mappings: prev.mappings.filter((m) =>
                                                    m.providerId !== p.id) } : prev
                                    );
                                    await Promise.all(
                                        states.map((st) =>
                                            removeProviderState({
                                                variables: { providerId: p.id, state: st },
                                            })
                                        )
                                    );
                                }
                                // toggle status
                                const { data } = await setProviderStatus({ variables: { id: p.id, status: next } });
                                if (!data?.acaAdminSetProviderStatus?.success) {
                                    presentAlert(data?.acaAdminSetProviderStatus?.error || "Failed to update provider");
                                } else {
                                    // optimistic status change
                                    setSnapshot((prev) =>
                                        prev ? {...prev, providers: prev.providers.map((pr) =>
                                                pr.id === p.id ? { ...pr, status: next } : pr),} : prev
                                    );
                                    void softRefresh();
                                }
                            } catch (e: any) {
                                console.error(e);
                                presentAlert(e.message || "Failed to update provider active status");
                            } finally {
                                setIsLoading(false);
                            }
                        },
                    },
                ],
            });
            return;
        }
        presentAlert({
            header: "Activate provider?",
            message: `Set “${p.name}” to ACTIVE?`,
            buttons: [
                { text: "Cancel", role: "cancel" },
                {
                    text: "Activate",
                    role: "confirm",
                    handler: async () => {
                        setIsLoading(true);
                        try {
                            const { data } = await setProviderStatus({ variables: { id: p.id, status: next } });
                            if (!data?.acaAdminSetProviderStatus?.success) {
                                presentAlert(data?.acaAdminSetProviderStatus?.error || "Failed to update provider");
                            } else {
                                setSnapshot((prev) =>
                                    prev ? {...prev, providers: prev.providers.map((pr) =>
                                            pr.id === p.id ? { ...pr, status: next } : pr),} : prev
                                );
                                void softRefresh();
                            }
                        } catch (e: any) {
                            console.error(e);
                            presentAlert(e.message || "Failed to update provider active status");
                        } finally {
                            setIsLoading(false);
                        }
                    },
                },
            ],
        });
    };

    // open provider add/remove modal for the given state.
    const onOpenAddMappingModal = (st: string) => {
        const hasActiveProviders = providers.some((provider) => provider.status === "ACTIVE");
        const mappedForState = mappings.filter((mapping) => mapping.state === st);

        // If no providers and nothing mapped, there's nothing to manage
        if (!hasActiveProviders && mappedForState.length === 0) {
            presentAlert(
                "There are no providers to manage for this state. Add providers first from the Providers section."
            );
            return;
        }

        setPendingMappingState(st);
        setMappingSelectOpen(true);
    };

    const closeMappingModal = () => {
        setMappingSelectOpen(false);
        setPendingMappingState(null);
    };

    const handleConfirmCreateProvider = async (value: string): Promise<boolean> => {
        setIsLoading(true);
        try {
            const { data } = await createProvider({ variables: { name: value } });
            if (!data?.acaAdminCreateProvider?.success) {
                presentAlert(data?.acaAdminCreateProvider?.error || "Failed to create provider");
                return false;
            }

            setNewProviderName("");
            await refresh();
            return true;
        } catch (e: any) {
            console.error(e);
            presentAlert(e.message || "Failed to add provider.");
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddProviderMapping = async (state: string, providerId: number): Promise<boolean> => {
        const provName = getProviderName(providerId);

        setIsLoading(true);
        try {
            const { data } = await addProviderState({
                variables: { providerId, state },
            });

            const res = data?.acaAdminAddProviderState;
            if (!res?.success) {
                presentAlert(res?.error || "Failed to add mapping");
                return false;
            }

            setSnapshot((prev) =>
                prev ? {...prev, mappings: [...prev.mappings, { providerId, state, name: provName }],} : prev
            );

            void softRefresh();
            return true;
        } catch (e: any) {
            console.error(e);
            presentAlert(e?.message || "Failed to add mapping");
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveProviderMapping = async (state: string, providerId: number): Promise<boolean> => {
        setIsLoading(true);
        try {
            const { data } = await removeProviderState({ variables: { providerId, state } });
            if (!data?.acaAdminRemoveProviderState?.success) {
                presentAlert(data?.acaAdminRemoveProviderState?.error || "Failed to remove mapping");
                return false;
            }

            setSnapshot((prev) =>
                prev ? {...prev, mappings: prev.mappings.filter((m) =>
                        !(m.state === state && m.providerId === providerId)),} : prev
            );
            void softRefresh();
            return true;
        } catch (e: any) {
            console.error(e);
            presentAlert(e.message || "Failed to remove provider to state mapping");
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmCreateFailedReason = async (value: string): Promise<boolean> => {
        setIsLoading(true);
        try {
            const { data } = await addFailedReason({ variables: { description: value } });
            if (!data?.acaAdminAddFailedReason?.success) {
                presentAlert(data?.acaAdminAddFailedReason?.error || "Failed to add code");
                return false;
            }

            setNewReasonDesc("");
            await refresh();
            return true;
        } catch (e: any) {
            console.error(e);
            presentAlert(e.message || "Failed to add failed code.");
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmCreateComplianceReason = async (value: string): Promise<boolean> => {
        setIsLoading(true);
        try {
            const { data } = await addComplianceReason({ variables: { description: value } });
            if (!data?.acaAdminAddComplianceReason?.success) {
                presentAlert(data?.acaAdminAddComplianceReason?.error || "Failed to add compliance code");
                return false;
            }

            setNewComplianceDesc("");
            await refresh();
            return true;
        } catch (e: any) {
            console.error(e);
            presentAlert(e.message || "Failed to add compliance code.");
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const onToggleCompliance = async (c: Reason, ev?: ToggleCustomEvent) => {
        if (ev) {
            ev.target.checked = (c.status === "ACTIVE");
        }

        const next = c.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        presentAlert({
            header: `${next === "ACTIVE" ? "Activate" : "Deactivate"} compliance code?`,
            message: `Set “${c.description}” to ${next}?`,
            buttons: [
                { text: "Cancel", role: "cancel" },
                {
                    text: next === "ACTIVE" ? "Activate" : "Deactivate",
                    role: next === "ACTIVE" ? "confirm" : "destructive",
                    handler: async () => {
                        setIsLoading(true);
                        try {
                            const { data } = await setComplianceReasonStatus({ variables: { id: c.id, status: next } });
                            if (!data?.acaAdminSetComplianceReasonStatus?.success) {
                                presentAlert(data?.acaAdminSetComplianceReasonStatus?.error || "Failed to update compliance code");
                            } else {
                                await refresh();
                            }
                        } catch (e: any) {
                            console.error(e);
                            presentAlert(e.message || "Failed to update compliance code active/inactive");
                        } finally {
                            setIsLoading(false);
                        }
                    },
                },
            ],
        });
    };
    const onToggleCode = async (c: Code, ev?: ToggleCustomEvent) => {
        // snap toggle back immediately and only flip after confirm
        if (ev) {
            ev.target.checked = (c.status === "ACTIVE");
        }

        const next = c.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        presentAlert({
            header: `${next === "ACTIVE" ? "Activate" : "Deactivate"} reason?`,
            message: `Set “${c.description}” to ${next}?`,
            buttons: [
                { text: "Cancel", role: "cancel" },
                {
                    text: next === "ACTIVE" ? "Activate" : "Deactivate",
                    role: next === "ACTIVE" ? "confirm" : "destructive",
                    handler: async () => {
                        setIsLoading(true);
                        try {
                            const { data } = await setFailedReasonStatus({ variables: { id: c.id, status: next } });
                            if (!data?.acaAdminSetFailedReasonStatus?.success) {
                                presentAlert(data?.acaAdminSetFailedReasonStatus?.error || "Failed to update code");
                            } else {
                                await refresh();
                            }
                        } catch (e: any) {
                            console.error(e);
                            presentAlert(e.message || "Failed to update failure code active/inactive");
                        } finally {
                            setIsLoading(false);
                        }
                    },
                },
            ],
        });
    };
    const handleCreateTier2Agent = async () => {
        if (!tier2NewUserId || !tier2NewNpn.trim()) {
            presentAlert("Please select an existing user and enter an NPN number.");
            return;
        }

        const npnError = validateGlobalNpn(tier2NewNpn);
        if (npnError) {
            presentAlert(npnError);
            return;
        }

        const selectedUser = tier2UserOptions.find((u) => u.id === tier2NewUserId);
        const userLabel = selectedUser
            ? `${selectedUser.displayName} (${selectedUser.email})`
            : `User ID ${tier2NewUserId}`;

        const selectedUserEmailKey = normalizeDupKey(selectedUser?.email || "");
        if (selectedUserEmailKey) {
            const existingAgentForUser = (tier2Agents || []).find((a) => normalizeDupKey(a.email) === selectedUserEmailKey);
            if (existingAgentForUser) {
                presentAlert("Tier 2 agent already exists for this user.");
                return;
            }
        }

        const npnKey = tier2NewNpn.trim();
        if (npnKey) {
            const existingAgentForNpn = (tier2Agents || []).find((a) => ((a.npnNumber || "").toString().trim() === npnKey));
            if (existingAgentForNpn) {
                presentAlert("NPN already exists for another agent.");
                return;
            }
        }

        const licensesToCreate = newAgentLicenses
            .filter((l) => l.stateCode && l.licenseNumber.trim())
            .map((l) => ({
                stateCode: l.stateCode,
                licenseNumber: l.licenseNumber.trim(),
                expirationDate: l.expirationDate || null,
                active: true, // active by default
            }));

        if (licensesToCreate.length) {
            const seen = new Set<string>();
            const dupStates: string[] = [];
            for (const l of licensesToCreate) {
                const k = l.stateCode;
                if (seen.has(k)) {
                    dupStates.push(k);
                } else {
                    seen.add(k);
                }
            }
            if (dupStates.length) {
                presentAlert(`Duplicate state(s) in licenses: ${dupStates.join(", ")}`);
                return;
            }
        }

        const licensesSummary = licensesToCreate.length
            ? licensesToCreate
                .map((l) => {
                    const exp = l.expirationDate ? ` (exp ${l.expirationDate})` : "";
                    return `${l.stateCode}: ${l.licenseNumber}${exp}`;
                })
                .join("\n")
            : "None";

        presentAlert({
            header: "Create Tier 2 agent?",
            message:
                `User: ${userLabel}\n` +
                `NPN: ${tier2NewNpn.trim()}\n` +
                `Licenses:\n${licensesSummary}`,
            buttons: [
                { text: "Cancel", role: "cancel" },
                {
                    text: "Create",
                    role: "confirm",
                    handler: () => {
                        void (async () => {
                            setIsLoading(true);
                            try {
                                // create agent (also active by default)
                                const { data } = await createTier2Agent({
                                    variables: {
                                        userId: tier2NewUserId,
                                        npnNumber: tier2NewNpn.trim(),
                                        agentActive: true,
                                    },
                                });

                                const res = data?.acaAdminCreateTier2Agent;
                                if (!res?.success) {
                                    presentAlert(res?.error || "Failed to create Tier 2 agent");
                                    return;
                                }

                                const createdAgentId = res.agent?.id;

                                // add new licenses from the modal
                                if (createdAgentId && licensesToCreate.length) {
                                    const results = await Promise.allSettled(
                                        licensesToCreate.map((lic) =>
                                            addTier2License({
                                                variables: {
                                                    agentId: createdAgentId,
                                                    stateCode: lic.stateCode,
                                                    licenseNumber: lic.licenseNumber,
                                                    expirationDate: lic.expirationDate,
                                                    active: true,
                                                },
                                            })
                                        )
                                    );

                                    const failed = results.filter((r) => r.status === "rejected");
                                    if (failed.length) {
                                        presentAlert(
                                            `Agent created, but ${failed.length} license(s) failed to add. You can manage licenses from the agent row.`
                                        );
                                    }
                                }

                                await softRefresh();

                                setTier2AddOpen(false);
                                resetNewAgentForm();
                            } catch (e: any) {
                                console.error(e);
                                presentAlert(e.message || "Failed to create Tier 2 agent");
                            } finally {
                                setIsLoading(false);
                            }
                        })();
                    },
                },
            ],
        });
    };

    const onOpenLicenseModal = (agent: Tier2AgentUiModel) => {
        setSelectedAgentForLicenses(agent);

        setIsEditingAgentNpn(false);
        setAgentNpnDraft(agent.npnNumber || "");
        setAgentNpnDraftError(validateGlobalNpn(agent.npnNumber || ""));

        setEditingLicenseId(null);
        setEditingLicenseDraft(null);
        setEditingLicenseDraftError("");

        setNewLicenseState("");
        setNewLicenseNumber("");
        setNewLicenseExpiry("");

        setLicenseModalOpen(true);
    };


    const onCloseLicenseModal = () => {
        setLicenseModalOpen(false);
        setSelectedAgentForLicenses(null);
    };

    // Update the global NPN for the selected agent
    const onSaveAgentNpn = async (newNpnRaw: string) => {
        if (!selectedAgentForLicenses) {
            return;
        }

        const trimmed = newNpnRaw.trim();

        const err = validateGlobalNpn(trimmed);
        if (err) {
            presentAlert(err);
            return;
        }

        const dupNpn = (tier2Agents || []).some((a) =>
            a.id !== selectedAgentForLicenses.id && (a.npnNumber || "").toString().trim() === trimmed);
        if (dupNpn) {
            presentAlert("NPN already exists for another agent.");
            return;
        }

        setIsLoading(true);
        try {
            const { data } = await setTier2AgentNpnNumber({
                variables: {
                    agentId: selectedAgentForLicenses.id,
                    npnNumber: trimmed,
                },
            });

            const res = data?.acaAdminSetTier2AgentNpnNumber;
            if (!res?.success) {
                presentAlert(res?.error || "Failed to update NPN");
                return;
            }

            const updatedAgent: Tier2AgentUiModel = {
                ...selectedAgentForLicenses,
                npnNumber: trimmed || null,
            };

            setSelectedAgentForLicenses(updatedAgent);
            setTier2Agents((prev) =>
                prev.map((a) => (a.id === updatedAgent.id ? updatedAgent : a))
            );

            setIsEditingAgentNpn(false);
            setAgentNpnDraft(updatedAgent.npnNumber || "");
            setAgentNpnDraftError(validateGlobalNpn(updatedAgent.npnNumber || ""));
        } catch (e: any) {
            console.error(e);
            presentAlert(e.message || "Failed to update NPN");
        } finally {
            setIsLoading(false);
        }
    };

    const onAddAgentLicense = async () => {
        if (!selectedAgentForLicenses) {
            return;
        }

        if (!newLicenseState || !newLicenseNumber.trim()) {
            presentAlert("State and license number are required.");
            return;
        }

        const existing = (selectedAgentForLicenses.licenses || []).some((l) => l.stateCode === newLicenseState);
        if (existing) {
            presentAlert("License already exists for this agent/state.");
            return;
        }

        const licenseNumber = newLicenseNumber.trim();
        const expirationDate = newLicenseExpiry || null;

        presentAlert({
            header: "Add new license?",
            message:
                `Agent: ${selectedAgentForLicenses.displayName}\n` +
                `State: ${newLicenseState}\n` +
                `License #: ${licenseNumber}\n` +
                `Expiration: ${expirationDate || "None"}\n` +
                `Status: Active`,
            buttons: [
                { text: "Cancel", role: "cancel" },
                {
                    text: "Add",
                    role: "confirm",
                    handler: () => {
                        void (async () => {
                            setIsLoading(true);
                            try {
                                const { data } = await addTier2License({
                                    variables: {
                                        agentId: selectedAgentForLicenses.id,
                                        stateCode: newLicenseState,
                                        licenseNumber,
                                        expirationDate,
                                        active: true,
                                    },
                                });

                                const res = data?.acaAdminAddTier2License;
                                if (!res?.success) {
                                    presentAlert(res?.error || "Failed to add license");
                                    return;
                                }

                                const newLic: AgentLicenseUiModel = res.license;
                                const updatedLicenses = [
                                    ...(selectedAgentForLicenses.licenses || []),
                                    newLic,
                                ];

                                const updatedAgent: Tier2AgentUiModel = {
                                    ...selectedAgentForLicenses,
                                    licenses: updatedLicenses,
                                    stateSummary: computeStateSummary(updatedLicenses),
                                };

                                setSelectedAgentForLicenses(updatedAgent);
                                setTier2Agents((prev) =>
                                    prev.map((a) => (a.id === updatedAgent.id ? updatedAgent : a))
                                );

                                setNewLicenseState("");
                                setNewLicenseNumber("");
                                setNewLicenseExpiry("");
                            } catch (e: any) {
                                console.error(e);
                                presentAlert(e.message || "Failed to add license");
                            } finally {
                                setIsLoading(false);
                            }
                        })();
                    },
                },
            ],
        });
    };

    const beginEditExistingLicense = (lic: AgentLicenseUiModel) => {
        setEditingLicenseId(lic.id);
        setEditingLicenseDraft({
            stateCode: lic.stateCode || "",
            licenseNumber: lic.licenseNumber || "",
            expirationDate: lic.expirationDate || "",
            active: !!lic.active,
        });
        setEditingLicenseDraftError("");
    };

    const cancelEditExistingLicense = () => {
        setEditingLicenseId(null);
        setEditingLicenseDraft(null);
        setEditingLicenseDraftError("");
    };

    const saveExistingLicenseEdits = async (original: AgentLicenseUiModel) => {
        if (!selectedAgentForLicenses) {
            return;
        }
        if (!editingLicenseDraft) {
            return;
        }

        const nextStateCode = (editingLicenseDraft.stateCode || "").toString().trim().toUpperCase();
        const nextLicenseNumber = (editingLicenseDraft.licenseNumber || "").toString().trim();
        const nextExpirationDate = (editingLicenseDraft.expirationDate || "").toString().trim();
        const nextActive = !!editingLicenseDraft.active;

        if (!nextStateCode) {
            setEditingLicenseDraftError("State is required.");
            return;
        }
        if (!nextLicenseNumber) {
            setEditingLicenseDraftError("License number is required.");
            return;
        }
        const hasDupState = (selectedAgentForLicenses.licenses || []).some((l) =>
            l.id !== original.id && (l.stateCode) === (nextStateCode)
        );
        if (hasDupState) {
            setEditingLicenseDraftError("License already exists for this agent/state.");
            return;
        }

        const oldExpiration = original.expirationDate || "";
        const oldStatus = original.active ? "Active" : "Inactive";
        const newStatus = nextActive ? "Active" : "Inactive";

        presentAlert({
            header: "Save license changes?",
            message:
                `State: ${original.stateCode} → ${nextStateCode}\n` +
                `License #: ${original.licenseNumber} → ${nextLicenseNumber}\n` +
                `Expiration: ${oldExpiration || "None"} → ${nextExpirationDate || "None"}\n` +
                `Status: ${oldStatus} → ${newStatus}`,
            buttons: [
                { text: "Cancel", role: "cancel" },
                {
                    text: "Save",
                    role: "confirm",
                    handler: () => {
                        void (async () => {
                            setIsLoading(true);
                            try {
                                const { data } = await updateTier2License({
                                    variables: {
                                        licenseId: original.id,
                                        stateCode: nextStateCode,
                                        licenseNumber: nextLicenseNumber,
                                        expirationDate: nextExpirationDate || null,
                                        active: nextActive,
                                    },
                                });

                                const res = data?.acaAdminUpdateTier2License;
                                if (!res?.success) {
                                    presentAlert(res?.error || "Failed to save license changes");
                                    return;
                                }

                                const updatedFromServer: AgentLicenseUiModel | null = res.license
                                    ? {
                                        id: res.license.id,
                                        stateCode: res.license.stateCode,
                                        licenseNumber: res.license.licenseNumber,
                                        expirationDate: res.license.expirationDate ?? null,
                                        active: !!res.license.active,
                                    }
                                    : null;

                                const updatedLicenses = (selectedAgentForLicenses.licenses || []).map((lic) => {
                                    if (lic.id !== original.id) {
                                        return lic;
                                    }
                                    return updatedFromServer || {
                                        ...lic,
                                        stateCode: nextStateCode,
                                        licenseNumber: nextLicenseNumber,
                                        expirationDate: nextExpirationDate || null,
                                        active: nextActive,
                                    };
                                });

                                const updatedAgent: Tier2AgentUiModel = {
                                    ...selectedAgentForLicenses,
                                    licenses: updatedLicenses,
                                    stateSummary: computeStateSummary(updatedLicenses),
                                };

                                setSelectedAgentForLicenses(updatedAgent);
                                setTier2Agents((prev) =>
                                    prev.map((a) => (a.id === updatedAgent.id ? updatedAgent : a))
                                );

                                cancelEditExistingLicense();
                            } catch (e: any) {
                                console.error(e);
                                presentAlert(e.message || "Failed to save license changes");
                            } finally {
                                setIsLoading(false);
                            }
                        })();
                    },
                },
            ],
        });
    };


    return (
        <IonPage>
            <AcaHeader pageTitle="ACA Admin Settings" />
            <IonLoading isOpen={isLoading} />
            <IonContent fullscreen className="aca-detail">

                <IonAccordionGroup  style={{ padding: 8, display: "grid", gap: 12 }} multiple>
                    <AcaSettingsComplianceSection
                        data={{ reasons: complianceReasons }}
                        actions={{
                            openAdd: () => setCompPromptOpen(true),
                            toggleReason: onToggleCompliance,
                        }}
                    />

                    <AcaSettingsFailedReasonSection
                        data={{ codes }}
                        actions={{
                            openAdd: () => setReasonPromptOpen(true),
                            toggleCode: onToggleCode,
                        }}
                    />

                    <AcaSettingsProvidersSection
                        data={{ providers, mappings }}
                        actions={{
                            openAdd: () => setProvPromptOpen(true),
                            toggleProvider: onToggleProvider,
                        }}
                    />

                    <AcaSettingsProviderStatesSection
                        data={{ providers, mappings }}
                        actions={{
                            openAddMappingModal: onOpenAddMappingModal,
                        }}
                    />

                    <AcaSettingsTier2AgentsSection
                        data={{ agents: tier2Agents }}
                        actions={{
                            openAddAgent: handleOpenAddTier2AgentModal,
                            openLicenseModal: onOpenLicenseModal,
                        }}
                    />
                </IonAccordionGroup>
                <AcaSettingsCreateAlertFlow
                    isPromptOpen={provPromptOpen}
                    setPromptOpen={setProvPromptOpen}
                    isConfirmOpen={provConfirmOpen}
                    setConfirmOpen={setProvConfirmOpen}
                    confirmQueued={provConfirmQueued}
                    setConfirmQueued={setProvConfirmQueued}
                    pendingValue={pendingProviderName}
                    setPendingValue={setPendingProviderName}
                    initialValue={newProviderName}
                    existingValues={(snapshot?.providers || []).map((provider) => provider.name)}
                    promptHeader="New Provider"
                    inputName="name"
                    inputPlaceholder="Provider name"
                    emptyMessage="Please enter a provider name."
                    duplicateMessage={(value) => `Provider “${value}” already exists.`}
                    confirmHeader="Add provider?"
                    confirmMessage={(value) => `Create provider “${value}”?`}
                    confirmButtonText="Add"
                    presentAlert={presentAlert}
                    onConfirm={handleConfirmCreateProvider}
                />
                <ModalManageProviderMappings
                    modal={{
                        isOpen: mappingSelectOpen,
                        onDidDismiss: closeMappingModal,
                    }}
                    data={{
                        pendingMappingState,
                        providers,
                        mappings,
                    }}
                    actions={{
                        onRemoveMapping: handleRemoveProviderMapping,
                        onAddMapping: handleAddProviderMapping,
                    }}
                />
                <AcaSettingsCreateAlertFlow
                    isPromptOpen={reasonPromptOpen}
                    setPromptOpen={setReasonPromptOpen}
                    isConfirmOpen={reasonConfirmOpen}
                    setConfirmOpen={setReasonConfirmOpen}
                    confirmQueued={reasonConfirmQueued}
                    setConfirmQueued={setReasonConfirmQueued}
                    pendingValue={pendingReasonDesc}
                    setPendingValue={setPendingReasonDesc}
                    initialValue={newReasonDesc}
                    existingValues={codes.map((code) => code.description)}
                    promptHeader="New Failed Reason"
                    inputName="desc"
                    inputPlaceholder="Description"
                    emptyMessage="Please enter a description."
                    duplicateMessage={(value) => `Failed reason “${value}” already exists.`}
                    confirmHeader="Add reason?"
                    confirmMessage={(value) => `Add failed reason “${value}”?`}
                    confirmButtonText="Add"
                    presentAlert={presentAlert}
                    onConfirm={handleConfirmCreateFailedReason}
                />

                <AcaSettingsCreateAlertFlow
                    isPromptOpen={compPromptOpen}
                    setPromptOpen={setCompPromptOpen}
                    isConfirmOpen={compConfirmOpen}
                    setConfirmOpen={setCompConfirmOpen}
                    confirmQueued={compConfirmQueued}
                    setConfirmQueued={setCompConfirmQueued}
                    pendingValue={pendingComplianceDesc}
                    setPendingValue={setPendingComplianceDesc}
                    initialValue={newComplianceDesc}
                    existingValues={complianceReasons.map((reason) => reason.description)}
                    promptHeader="New Compliance Code"
                    inputName="desc"
                    inputPlaceholder="Description"
                    emptyMessage="Please enter a description."
                    duplicateMessage={(value) => `Compliance code “${value}” already exists.`}
                    confirmHeader="Add compliance code?"
                    confirmMessage={(value) => `Add “${value}”?`}
                    confirmButtonText="Add"
                    presentAlert={presentAlert}
                    onConfirm={handleConfirmCreateComplianceReason}
                />

                <ModalAddTier2Agent
                    modal={{
                        isOpen: tier2AddOpen,
                        onCancel: handleCloseAddTier2AgentModal,
                    }}
                    data={{
                        tier2UserOptions,
                        newAgentLicenses,
                    }}
                    form={{
                        tier2NewUserId,
                        setTier2NewUserId,
                        tier2NewNpn,
                        setTier2NewNpn,
                        tier2NewNpnError,
                    }}
                    actions={{
                        updateNewAgentLicenseRow,
                        removeNewAgentLicenseRow,
                        addNewAgentLicenseRow,
                        onSave: handleCreateTier2Agent,
                    }}
                />

                <ModalManageTier2Licenses
                    modal={{
                        isOpen: licenseModalOpen,
                        onClose: onCloseLicenseModal,
                    }}
                    data={{
                        selectedAgentForLicenses,
                    }}
                    npnEditor={{
                        isEditingAgentNpn,
                        setIsEditingAgentNpn,
                        agentNpnDraft,
                        setAgentNpnDraft,
                        agentNpnDraftError,
                        setAgentNpnDraftError,
                    }}
                    newLicenseForm={{
                        newLicenseState,
                        setNewLicenseState,
                        newLicenseNumber,
                        setNewLicenseNumber,
                        newLicenseExpiry,
                        setNewLicenseExpiry,
                    }}
                    editingLicense={{
                        editingLicenseId,
                        editingLicenseDraft,
                        setEditingLicenseDraft,
                        editingLicenseDraftError,
                        setEditingLicenseDraftError,
                    }}
                    actions={{
                        validateGlobalNpn,
                        presentAlert,
                        onSaveAgentNpn,
                        onToggleTier2AgentActive,
                        onAddAgentLicense,
                        beginEditExistingLicense,
                        cancelEditExistingLicense,
                        saveExistingLicenseEdits,
                    }}
                />

            </IonContent>
        </IonPage>
    );
};

export default AcaSettings;